import React, { Component } from "react";
import AnswerPopup from "./Components/AnswerPopup/AnswerPopup";
import Comments from "./Components/Comments/Comments";
import ExitCasePopup from "./Components/ExitCasePopup/ExitCasePopup";
import LabPanels from "./Components/LabPanels/LabPanels";
import SimulationBody from "./Components/SimulationBody/SimulationBody";
import SimulationFooter from "./Components/SimulationFooter/SimulationFooter";
import SimulationHeader from "./Components/SimulationHeader/SimulationHeader";
import "./Simulation.css";
import logFetchError from "../../Functions/LogFetchError";
import HelpPopup from "./Components/HelpPopup/HelpPopup";

export default class Simulation extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedCheckboxAmount: 0,
      displayCommentsPopup: false,
      comments: "",
      displayLabPanels: false,
      timeRemaining: props.simulationData.timeRemainingInSeconds,
      currentQuestion: props.simulationData.currentQuestion,
      submitStep: false,
      answerList: props.simulationData.answerList,
      displayAnswerPopup: false,
      displayExitCasePrompt: false,
      gradeOutput: [],
      caseEnded: false,
      showHelpPopup: false,
      // Text modification states
      textEditModes: {
        highlight: false,
        strikeThrough: false,
      },
      textModificationsById: {}, // Store text modifications per question
    };

    this.modifiedKeyIndexes = 0;
  }

  timer = null;

  componentDidMount() {
    if (this.timer === null && this.props.timedExam) {
      this.startCaseTimer();
    }

    // Add copy protection
    this.addCopyProtection();
  }

  componentWillUnmount() {
    this.removeCopyProtection();
    // Clean up text selection listeners
    document.removeEventListener("mouseup", this.handleTextSelection);
    document.removeEventListener("touchend", this.handleTextSelection);
  }

  // Copy protection methods
  addCopyProtection = () => {
    // Disable right-click context menu
    this.disableContextMenu = (event) => event.preventDefault();
    document.addEventListener("contextmenu", this.disableContextMenu);

    // Disable copy and cut keyboard shortcuts
    this.disableCopyCut = (event) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        (event.key === "c" || event.key === "x")
      ) {
        event.preventDefault();
      }
    };
    document.addEventListener("keydown", this.disableCopyCut);

    // Disable the copy event
    this.disableCopyEvent = (event) => {
      event.preventDefault();
    };
    document.addEventListener("copy", this.disableCopyEvent);
  };

  removeCopyProtection = () => {
    if (this.disableContextMenu) {
      document.removeEventListener("contextmenu", this.disableContextMenu);
    }
    if (this.disableCopyCut) {
      document.removeEventListener("keydown", this.disableCopyCut);
    }
    if (this.disableCopyEvent) {
      document.removeEventListener("copy", this.disableCopyEvent);
    }
  };

  // Helper functions from original implementation
  getSelectedContainers = (range) => {
    const containers = [];
    const startContainer = range.startContainer;
    const endContainer = range.endContainer;

    if (startContainer === endContainer) {
      containers.push(startContainer);
    } else {
      // Create a tree walker to find all text nodes in the range
      const walker = document.createTreeWalker(
        range.commonAncestorContainer,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: function (node) {
            // Check if this text node intersects with our range
            const nodeRange = document.createRange();
            nodeRange.selectNodeContents(node);

            if (
              range.compareBoundaryPoints(Range.END_TO_START, nodeRange) <= 0 &&
              range.compareBoundaryPoints(Range.START_TO_END, nodeRange) >= 0
            ) {
              return NodeFilter.FILTER_ACCEPT;
            }
            return NodeFilter.FILTER_REJECT;
          },
        },
        false
      );

      let node;
      while ((node = walker.nextNode())) {
        containers.push(node);
      }
    }

    return containers;
  };

  // Replace your getDistanceToStartOfContainerBreak function with this:
  getDistanceToStartOfContainerBreak = (container) => {
    // If the container is a text node, check if its parent has a sortingOrder
    let parentElement = container.parentNode;

    // Walk up until we find an element with data-id containing sortingOrder
    while (parentElement && parentElement !== document) {
      if (
        parentElement.nodeType === Node.ELEMENT_NODE &&
        parentElement.getAttribute &&
        parentElement.getAttribute("data-id")
      ) {
        const dataId = parentElement.getAttribute("data-id");

        // Check if this data-id contains sortingOrder
        if (dataId.includes("-sortingOrder:")) {
          // Extract the offset from the sortingOrder
          const match = dataId.match(/-sortingOrder:(\d+)/);
          if (match) {
            return parseInt(match[1], 10);
          }
        }
      }
      parentElement = parentElement.parentNode;
    }

    // If no sortingOrder found, this is original unhighlighted text, return 0
    return 0;
  };

  // Update your handleTextSelection function's offset calculation:
  handleTextSelection = (event) => {
    if (
      event &&
      event.target &&
      (event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.isContentEditable)
    ) {
      return;
    }
    const selection = window.getSelection();
    let removeMode = false;
    const currentMode = this.state.textEditModes.highlight
      ? "highlight"
      : "strikethrough";

    if (selection.rangeCount > 0) {
      const selectedTextByContainer = {};

      for (let i = 0; i < selection.rangeCount; i++) {
        const range = selection.getRangeAt(i);

        // Function to get the parent element with data-id (keep your existing one)
        const getParentWithDataId = (node) => {
          while (node) {
            if (
              node.getAttribute &&
              node.getAttribute("data-id") !== null &&
              node.getAttribute("data-id") !== "container-break" &&
              !node.getAttribute("data-id").includes("sortingOrder")
            ) {
              return node;
            }
            node = node.parentNode;
          }
          return null;
        };

        // Get all text nodes in the selection
        const containers = this.getSelectedContainers(range);

        containers.forEach((container) => {
          const parentWithDataId = getParentWithDataId(container);
          if (!parentWithDataId) return;

          const text = container.textContent;

          // Get the base offset from any sortingOrder in the hierarchy
          const baseOffset = this.getDistanceToStartOfContainerBreak(container);

          let rangeStart, rangeEnd;

          if (
            container === range.startContainer &&
            container === range.endContainer
          ) {
            // Selection within a single container
            rangeStart = baseOffset + range.startOffset;
            rangeEnd = baseOffset + range.endOffset;

            if (rangeStart === rangeEnd) {
              removeMode = true;
            }
          } else if (container === range.startContainer) {
            // This is the start container
            rangeStart = baseOffset + range.startOffset;
            rangeEnd = baseOffset + text.length;
          } else if (container === range.endContainer) {
            // This is the end container
            rangeStart = baseOffset;
            rangeEnd = baseOffset + range.endOffset;
          } else {
            // This is a middle container - select all of it
            rangeStart = baseOffset;
            rangeEnd = baseOffset + text.length;
          }

          const containerId = parentWithDataId.getAttribute("data-id");
          const selectedText = text.substring(
            rangeStart - baseOffset,
            rangeEnd - baseOffset
          );

          // Store or update the selection
          if (!selectedTextByContainer[containerId]) {
            selectedTextByContainer[containerId] = {
              text: selectedText,
              start: rangeStart,
              end: rangeEnd,
              mode: currentMode,
            };
          } else {
            // Update the end position if this container extends the selection
            selectedTextByContainer[containerId].text += selectedText;
            selectedTextByContainer[containerId].end = Math.max(
              selectedTextByContainer[containerId].end,
              rangeEnd
            );
          }
        });
      }

      // Rest of your setState logic remains the same...
      this.setState((prevState) => {
        const currentQuestionId = prevState.currentQuestion;
        const newTextModificationsById = { ...prevState.textModificationsById };

        if (!newTextModificationsById[currentQuestionId]) {
          newTextModificationsById[currentQuestionId] = {};
        }

        const textModificationsForQuestion =
          newTextModificationsById[currentQuestionId];

        if (removeMode) {
          const entries = Object.entries(selectedTextByContainer);
          if (entries.length > 0) {
            const [parentId, { start, end }] = entries[0];
            if (textModificationsForQuestion[parentId]) {
              textModificationsForQuestion[parentId] =
                textModificationsForQuestion[parentId].filter(
                  (range) => !(range.start <= start && range.end >= end)
                );
            }
          }
        } else {
          Object.entries(selectedTextByContainer).forEach(
            ([parentId, { start, end, mode }]) => {
              if (!textModificationsForQuestion[parentId]) {
                textModificationsForQuestion[parentId] = [];
              }

              const newRange = { start, end, mode };

              // Your existing mergeRanges function
              const mergeRanges = (existingRanges, newRange) => {
                const mergedRanges = [];
                let pushed = false;

                existingRanges.forEach((range) => {
                  if (range.mode === newRange.mode) {
                    if (
                      (newRange.start <= range.end &&
                        newRange.end >= range.start) ||
                      (newRange.start >= range.start &&
                        newRange.end <= range.end)
                    ) {
                      newRange.start = Math.min(newRange.start, range.start);
                      newRange.end = Math.max(newRange.end, range.end);
                    } else {
                      mergedRanges.push(range);
                    }
                  } else {
                    if (
                      newRange.start <= range.end &&
                      newRange.end >= range.start
                    ) {
                      if (
                        newRange.start > range.start &&
                        newRange.end < range.end
                      ) {
                        mergedRanges.push({
                          start: range.start,
                          end: newRange.start,
                          mode: range.mode,
                        });
                        mergedRanges.push({
                          start: newRange.end,
                          end: range.end,
                          mode: range.mode,
                        });
                      } else if (newRange.start > range.start) {
                        range.end = newRange.start;
                        mergedRanges.push(range);
                      } else if (newRange.end < range.end) {
                        range.start = newRange.end;
                        mergedRanges.push(range);
                      }
                      pushed = true;
                    } else {
                      mergedRanges.push(range);
                    }
                  }
                });

                mergedRanges.push(newRange);
                return mergedRanges;
              };

              textModificationsForQuestion[parentId] = mergeRanges(
                textModificationsForQuestion[parentId],
                newRange
              );
            }
          );
        }

        return {
          ...prevState,
          textModificationsById: newTextModificationsById,
        };
      });

      selection.removeAllRanges();
    }
  };

  // Helper function to apply highlighting for all highlighted texts
  getHighlightedText = (text, parentId) => {
    if (typeof text === "number") {
      text = String(text);
    }
    // Retrieve highlighted ranges for the specific parentId under the currentQuestion
    const currentQuestionId = this.state.currentQuestion;
    const highlightedRanges = [
      ...(this.state.textModificationsById[currentQuestionId]?.[parentId] ||
        []),
    ];

    // If no highlighted ranges for this element, return original trimmed text
    if (!highlightedRanges || highlightedRanges.length === 0) return text;

    // Create an array to hold parts of the text with highlighting
    const parts = [];
    let lastIndex = 0;
    const sortedRanges = highlightedRanges.sort((a, b) => a.start - b.start);

    // Iterate through the highlighted ranges
    sortedRanges.forEach(({ start, end, mode }) => {
      // Add text before the current highlight
      if (lastIndex < start) {
        this.modifiedKeyIndexes++;
        parts.push(
          <span
            data-id={`${parentId}-sortingOrder:${lastIndex}`}
            key={`${parentId}-sortingOrder:${this.modifiedKeyIndexes}`}
          >
            {text.slice(lastIndex, start)}
          </span>
        );
      }

      // Add the highlighted part of the text
      this.modifiedKeyIndexes++;
      parts.push(
        <span
          data-id={`${parentId}-sortingOrder:${start}`}
          key={`${parentId}-sortingOrder:${this.modifiedKeyIndexes}`}
          style={
            mode === "highlight"
              ? { backgroundColor: "yellow" }
              : { textDecoration: "line-through" }
          }
        >
          {text.slice(start, end)}
        </span>
      );

      // Update the last index
      lastIndex = end;
    });

    // Add any remaining text after the last highlight
    if (lastIndex < text.length) {
      this.modifiedKeyIndexes++;
      parts.push(
        <span
          data-id={`${parentId}-sortingOrder:${lastIndex}`}
          key={`${parentId}-sortingOrder:${this.modifiedKeyIndexes}`}
        >
          {text.slice(lastIndex)}
        </span>
      );
    }

    // Return a single element that represents the entire text with highlights
    return <>{parts}</>;
  };

  // Add event listeners for text selection when modes are active
  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.textEditModes.highlight !==
        this.state.textEditModes.highlight ||
      prevState.textEditModes.strikeThrough !==
        this.state.textEditModes.strikeThrough
    ) {
      if (
        this.state.textEditModes.highlight ||
        this.state.textEditModes.strikeThrough
      ) {
        document.addEventListener("mouseup", this.handleTextSelection);
        document.addEventListener("touchend", this.handleTextSelection);
      } else {
        document.removeEventListener("mouseup", this.handleTextSelection);
        document.removeEventListener("touchend", this.handleTextSelection);
      }
    }
  }

  // Toggle text edit modes
  toggleHighlight = () => {
    this.setState((prevState) => ({
      textEditModes: {
        highlight: !prevState.textEditModes.highlight,
        strikeThrough: false,
      },
    }));
  };

  toggleStrikeThrough = () => {
    this.setState((prevState) => ({
      textEditModes: {
        highlight: false,
        strikeThrough: !prevState.textEditModes.strikeThrough,
      },
    }));
  };

  startCaseTimer = () => {
    this.timer = setInterval(() => {
      let timeRemaining = this.state.timeRemaining;
      if (timeRemaining - 1 === 0) {
        this.setState({ caseEnded: true, timeRemaining: 0 });
        clearInterval(this.timer);
        this.handleAnswerSubmission(
          this.props.simulationData.answerList.length
        );
      } else {
        timeRemaining--;
        this.setState({ timeRemaining });
      }
    }, 1000);
  };

  pauseCaseTimer = () => {
    clearInterval(this.timer);
  };

  updateCheckboxAnswer = (optionName) => {
    let index = 0;
    let currentQuestion = this.state.currentQuestion - 1;
    let answerList = [...this.state.answerList];
    for (let i = 0; i < answerList[currentQuestion].length; ++i) {
      if (optionName === answerList[currentQuestion][i].option) {
        index = i;
        break;
      }
    }
    answerList[currentQuestion][index].selected =
      !answerList[currentQuestion][index].selected;
    let selectedCheckboxAmount = this.state.selectedCheckboxAmount;
    if (answerList[currentQuestion][index].selected) {
      selectedCheckboxAmount++;
    } else {
      selectedCheckboxAmount--;
    }
    this.setState({ answerList, selectedCheckboxAmount });
  };

  modifyInputAnswer = (e, index) => {
    let answerInfo = [...this.state.answerList];
    if (e.target.value.length <= 100) {
      answerInfo[this.state.currentQuestion - 1][index] = e.target.value;
      this.setState({ answerInfo });
    }
  };

  formatAnswer = (currentQuestion) => {
    let formattedAnswerList = [];
    this.state.answerList.forEach((value, index) => {
      if (index < currentQuestion) {
        let answer = {
          customerId: this.props.userData.CustomerId,
          caseId: this.props.selectedCaseId,
          questionId: this.props.simulationData.qo[index].QUESTIONID,
          caseguid: this.props.caseguid,
          customerAnswersBlank: null,
          customerAnswerMC: null,
          timeLeftInSec: this.state.timeRemaining,
          completeSubmission:
            index + 1 === this.props.simulationData.answerList.length
              ? true
              : false,
          isTimed: this.props.timedExam,
        };
        if (typeof value[0] === "string") {
          answer.customerAnswersBlank = [...value];
        } else {
          let multipleChoiceOptions = [];
          this.props.simulationData.ao.forEach((aoValue, aoIndex) => {
            if (aoValue.QUESTIONID === answer.questionId) {
              multipleChoiceOptions.push(aoValue);
            }
          });
          answer.customerAnswerMC = [];
          value.forEach((value, index) => {
            if (value.selected) {
              answer.customerAnswerMC.push(
                multipleChoiceOptions[index].ANSWERID
              );
            }
          });
        }
        formattedAnswerList.push(answer);
      }
    });

    if (currentQuestion === this.props.simulationData.answerList.length) {
      return formattedAnswerList;
    } else {
      return formattedAnswerList[formattedAnswerList.length - 1];
    }
  };

  handleAnswerSubmission = async (currentQuestion) => {
    if (currentQuestion === this.props.simulationData.answerList.length) {
      await this.fetchFinalQuestionSubmission(currentQuestion);
    } else {
      if (this.props.practiceMode) {
        await this.fetchPracticeModeSubmission(currentQuestion);
      } else {
        await this.fetchMoveOn(currentQuestion);
      }
    }
  };

  fetchPracticeModeSubmission = async (currentQuestion) => {
    this.props.setFetchOutstanding(true);
    let answer = this.formatAnswer(currentQuestion);
    await fetch(`${this.props.route}/moveon.webapi`, {
      method: "POST",
      headers: {
        Token: this.props.userData.Token,
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(answer),
    })
      .then((res) => {
        if (res.ok) {
          return res.text();
        } else if (res.status === 401) {
          this.props.setAuthenticationError();
        } else {
          throw new Error("Unable to submit answers");
        }
      })
      .then(async () => {
        await fetch(`${this.props.route}/grading.webapi`, {
          method: "POST",
          headers: {
            Token: this.props.userData.Token,
            "Content-Type": "text/plain",
          },
          body: JSON.stringify([answer]),
        })
          .then((res) => {
            if (res.ok) {
              return res.text();
            } else if (res.status === 401) {
              this.props.setAuthenticationError();
            } else {
              throw new Error("Unable to submit answers");
            }
          })
          .then((res) => {
            this.props.setFetchOutstanding(false);
            let result = JSON.parse(res);
            this.pauseCaseTimer();
            this.setState({
              displayAnswerPopup: true,
              gradeOutput: result.pgolist,
              selectedCheckboxAmount: 0,
            });
          });
      })
      .catch((error) => {
        this.props.setFetchOutstanding(false);
        console.log(error);
        logFetchError(
          error,
          this.props.userProfile,
          this.props.userData,
          "fetchPracticeModeSubmission"
        );
      });
  };

  fetchMoveOn = async (currentQuestion) => {
    this.props.setFetchOutstanding(true);
    let answer = this.formatAnswer(currentQuestion);
    await fetch(`${this.props.route}/moveon.webapi`, {
      method: "POST",
      headers: {
        Token: this.props.userData.Token,
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(answer),
    })
      .then((res) => {
        if (res.ok) {
          return res.text();
        } else if (res.status === 401) {
          this.props.setAuthenticationError();
        } else {
          throw new Error("Unable to submit answers");
        }
      })
      .then(() => {
        this.props.setFetchOutstanding(false);
        this.setState(
          {
            currentQuestion: currentQuestion + 1,
            submitStep: false,
            selectedCheckboxAmount: 0,
          },
          () => {
            let answerStart = document.getElementById("beginQuestionElement");
            let headerContainer = document.getElementById("headerContainer");
            window.scrollTo(
              0,
              answerStart.offsetTop - headerContainer.offsetHeight - 10
            );
          }
        );
      })
      .catch((error) => {
        this.props.setFetchOutstanding(false);
        console.log(error);
        logFetchError(
          error,
          this.props.userProfile,
          this.props.userData,
          "fetchMoveOn"
        );
      });
  };

  fetchFinalQuestionSubmission = async (currentQuestion) => {
    this.props.setFetchOutstanding(true);
    let answers = this.formatAnswer(currentQuestion);

    await fetch(`${this.props.route}/grading.webapi`, {
      method: "POST",
      headers: {
        Token: this.props.userData.Token,
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(answers),
    })
      .then((res) => {
        if (res.ok) {
          return res.text();
        } else if (res.status === 401) {
          this.props.setAuthenticationError();
        } else {
          throw new Error("Unable to submit answers");
        }
      })
      .then((res) => {
        this.props.setFetchOutstanding(false);
        let result = JSON.parse(res);
        this.props.displayGrading(result);
      })
      .catch((error) => {
        this.props.setFetchOutstanding(false);
        console.log(error);
        logFetchError(
          error,
          this.props.userProfile,
          this.props.userData,
          "fetchFinalQuestionSubmission"
        );
      });
  };

  continueCaseHandler = () => {
    if (
      this.state.currentQuestion ===
        this.props.simulationData.answerList.length ||
      this.state.caseEnded
    ) {
      this.props.displayGrading();
    } else {
      this.startCaseTimer();
      this.setState(
        {
          currentQuestion: this.state.currentQuestion + 1,
          submitStep: false,
          displayAnswerPopup: false,
        },
        () => {
          let answerStart = document.getElementById("beginQuestionElement");
          let headerContainer = document.getElementById("headerContainer");
          window.scrollTo(
            0,
            answerStart.offsetTop - headerContainer.offsetHeight - 10
          );
        }
      );
    }
  };

  render() {
    console.log(this.state.textModificationsById);
    return (
      <div className="simulationPageContainer">
        <SimulationHeader
          timedExam={this.props.timedExam}
          timeRemaining={this.state.timeRemaining}
          simulationData={this.props.simulationData}
          currentQuestion={this.state.currentQuestion}
          displayLabPanels={() => this.setState({ displayLabPanels: true })}
          timerSpeed={this.props.timerSpeed}
          textEditModes={this.state.textEditModes}
          toggleHighlight={this.toggleHighlight}
          toggleStrikeThrough={this.toggleStrikeThrough}
        />
        <SimulationBody
          updateCheckboxAnswer={this.updateCheckboxAnswer}
          selectedCheckboxAmount={this.state.selectedCheckboxAmount}
          simulationData={this.props.simulationData}
          currentQuestion={this.state.currentQuestion}
          modifyInputAnswer={this.modifyInputAnswer}
          answerList={this.state.answerList}
          caseNumber={this.props.caseData.ID}
          showHelpPopup={() => this.setState({ showHelpPopup: true })}
          getHighlightedText={this.getHighlightedText}
        />
        <SimulationFooter
          showComments={() => this.setState({ displayCommentsPopup: true })}
          submitStep={this.state.submitStep}
          updateSubmitStep={() => this.setState({ submitStep: true })}
          submitAnswer={() =>
            this.handleAnswerSubmission(this.state.currentQuestion)
          }
          displayExitCasePrompt={() =>
            this.setState({ displayExitCasePrompt: true })
          }
          displayLabPanels={() => this.setState({ displayLabPanels: true })}
        />
        <LabPanels
          displayLabPanels={this.state.displayLabPanels}
          closeLabPanels={() => this.setState({ displayLabPanels: false })}
        />
        <Comments
          displayCommentsPopup={this.state.displayCommentsPopup}
          comments={this.state.comments}
          updateComments={(event) =>
            this.setState({ comments: event.target.value })
          }
          closeComments={() => this.setState({ displayCommentsPopup: false })}
        />
        {this.state.displayAnswerPopup && (
          <AnswerPopup
            displayAnswerPopup={this.state.displayAnswerPopup}
            continueCase={this.continueCaseHandler}
            simulationData={this.props.simulationData}
            currentQuestion={this.state.currentQuestion}
            gradeOutput={this.state.gradeOutput}
            caseEnded={this.state.caseEnded}
          />
        )}
        <ExitCasePopup
          displayExitCasePrompt={this.state.displayExitCasePrompt}
          confirmExitCase={this.props.exitSimulation}
          cancelExitCase={() => this.setState({ displayExitCasePrompt: false })}
        />
        {this.state.showHelpPopup && (
          <HelpPopup
            closeHelpPopup={() => this.setState({ showHelpPopup: false })}
          />
        )}
      </div>
    );
  }
}
